import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Timeline as TimelineIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";
import { conversationHistoryApi } from "../services/aiDiagnosticApi";
import { withStyles } from "@mui/styles";
import { styleSheet } from "../style.js";

// Expandable JSON component
const ExpandableJson = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const jsonString = JSON.stringify(data, null, 2);
  const isLargeJson = jsonString.length > 500;

  if (!isLargeJson) {
    return (
      <pre style={{ 
        whiteSpace: 'pre', 
        fontSize: '12px',
        fontFamily: 'monospace',
        margin: 0,
        padding: '8px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        overflow: 'auto'
      }}>
        {jsonString}
      </pre>
    );
  }

  return (
    <Box>
      <Button
        size="small"
        onClick={() => setIsExpanded(!isExpanded)}
        startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        {isExpanded ? "Collapse JSON" : "Expand JSON"}
      </Button>
      <Collapse in={isExpanded}>
        <pre style={{ 
          whiteSpace: 'pre', 
          fontSize: '12px', 
          maxHeight: '400px', 
          overflow: 'auto',
          fontFamily: 'monospace',
          margin: 0,
          padding: '8px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}>
          {jsonString}
        </pre>
      </Collapse>
      {!isExpanded && (
        <Typography variant="body2" color="text.secondary">
          <pre style={{ 
            whiteSpace: 'pre', 
            fontSize: '12px', 
            margin: 0,
            fontFamily: 'monospace',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            padding: '8px',
            overflow: 'hidden'
          }}>
            {jsonString.substring(0, 300)}... [truncated]
          </pre>
        </Typography>
      )}
    </Box>
  );
};

const ConversationHistory = ({ classes, refreshTrigger, claudeToken }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [slackUserId, setSlackUserId] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [messages, setMessages] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Cache metrics summary
  const [cacheSummary, setCacheSummary] = useState({
    totalInputTokens: 0,
    totalCacheReadTokens: 0,
    totalSavings: 0,
    cachingEnabled: false,
  });

  const conversationListRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const observer = useRef(null);
  const messagesObserver = useRef(null);

  // Infinite scroll for conversations
  const lastConversationElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && pagination.hasNextPage) {
          fetchMoreConversations();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, pagination.hasNextPage]
  );

  // Infinite scroll for messages
  const lastMessageElementRef = useCallback(
    (node) => {
      if (!node || detailLoading || loadingMoreMessages || !selectedConversation) return;
      if (messagesObserver.current) messagesObserver.current.disconnect();

      messagesObserver.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && selectedConversation?.pagination?.hasNextPage) {
            fetchMoreMessages();
          }
        },
        {
          root: messagesContainerRef.current,
          rootMargin: "0px 0px 100px 0px",
          threshold: 0.1,
        }
      );

      messagesObserver.current.observe(node);
    },
    [detailLoading, loadingMoreMessages, selectedConversation]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchConversations();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-refresh when authentication changes (login/logout)
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('🔄 Auto-refreshing conversations due to authentication change');
      fetchConversations(true); // Reset pagination
      setSelectedConversation(null);
      setMessages([]);
    }
  }, [refreshTrigger]);

  // Auto-refresh when claudeToken changes (login/logout)
  useEffect(() => {
    if (claudeToken) {
      console.log('🔑 Claude token detected, refreshing conversations');
      fetchConversations(true); // Reset pagination
      setSelectedConversation(null);
      setMessages([]);
    }
  }, [claudeToken]);

  useEffect(() => {
    if (conversations.length > 0) {
      const totalInputTokens = conversations.reduce((sum, conv) => sum + (conv.inputTokens || 0), 0);
      const totalCacheReadTokens = conversations.reduce((sum, conv) => sum + (conv.cacheReadInputTokens || 0), 0);
      const totalSavings = conversations.reduce((sum, conv) => sum + (conv.cachedTokenSavings || 0), 0);
      const cachingEnabled = conversations.some((conv) => (conv.cacheReadInputTokens || 0) > 0);

      setCacheSummary({
        totalInputTokens,
        totalCacheReadTokens,
        totalSavings,
        cachingEnabled,
      });
    }
  }, [conversations]);

  const fetchConversations = async (resetPagination = true) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: resetPagination ? 1 : pagination.page,
        limit: pagination.limit,
        startDate,
        endDate,
        slackUserId,
      };

      const data = await conversationHistoryApi.getConversations(params);
      
      // Handle case where API returns undefined or unexpected data
      if (!data) {
        console.log('API returned no data, setting empty state');
        setConversations([]);
        setPagination(pagination);
        return;
      }
      
      if (resetPagination) {
        setConversations(data.conversations || []);
        setPagination(data.pagination || pagination);
      } else {
        setConversations(prev => [...prev, ...(data.conversations || [])]);
        setPagination(data.pagination || pagination);
      }
    } catch (err) {
      console.error("Error in fetchConversations:", err);
      setError(err.message || "Failed to fetch conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreConversations = async () => {
    if (loadingMore || !pagination.hasNextPage) return;
    
    try {
      setLoadingMore(true);
      const nextPage = pagination.page + 1;
      const params = {
        page: nextPage,
        limit: pagination.limit,
        startDate,
        endDate,
        slackUserId,
      };

      const data = await conversationHistoryApi.getConversations(params);
      setConversations(prev => [...prev, ...(data.conversations || [])]);
      setPagination(data.pagination || pagination);
    } catch (err) {
      console.error("Error fetching more conversations:", err);
      setError(err.message || "Failed to fetch more conversations");
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchConversationDetail = async (conversationId) => {
    try {
      setDetailLoading(true);
      setError(null);
      
      const data = await conversationHistoryApi.getConversationDetail(conversationId);
      setSelectedConversation(data);
      setMessages(data.messages || []);
    } catch (err) {
      console.error("Error in fetchConversationDetail:", err);
      setError(err.message || "Failed to fetch conversation detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchMoreMessages = async () => {
    if (loadingMoreMessages || !selectedConversation?.pagination?.hasNextPage) return;
    
    try {
      setLoadingMoreMessages(true);
      const nextPage = selectedConversation.pagination.page + 1;
      const params = {
        page: nextPage,
        limit: selectedConversation.pagination.limit,
      };

      const data = await conversationHistoryApi.getConversationDetail(selectedConversation.id, params);
      setMessages(prev => [...prev, ...(data.messages || [])]);
      setSelectedConversation(prev => ({
        ...prev,
        pagination: data.pagination,
      }));
    } catch (err) {
      console.error("Error fetching more messages:", err);
      setError(err.message || "Failed to fetch more messages");
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    fetchConversationDetail(conversation.id);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchConversations(true);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatTokenCount = (count) => {
    return count?.toLocaleString() || "0";
  };

  // Generate preview text from conversation data
  const generatePreview = (conversation) => {
    // Use the title as preview content (it contains the first message)
    if (conversation.title && conversation.title !== "Untitled Conversation") {
      // Clean up HTML entities and truncate to 100 characters
      const cleanTitle = conversation.title
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
      
      return cleanTitle.length > 100 
        ? cleanTitle.substring(0, 100) + '...' 
        : cleanTitle;
    }
    
    // Fallback to a generic description
    return `Conversation with ${conversation.messageCount} messages`;
  };

  if (loading && conversations.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading conversations...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorContainer}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Filters */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                <FilterListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Filters
              </Typography>
              <Button
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {showFilters ? "Hide" : "Show"} Filters
              </Button>
            </Box>
            
            <Collapse in={showFilters}>
              <form onSubmit={handleFilterSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Slack User ID"
                      value={slackUserId}
                      onChange={(e) => setSlackUserId(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{ height: '56px' }}
                    >
                      Apply Filters
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Collapse>
          </CardContent>
        </Card>
      </Grid>

      {/* Cache Summary */}
      {cacheSummary.cachingEnabled && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Cache Performance Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Input Tokens
                  </Typography>
                  <Typography variant="h6">
                    {formatTokenCount(cacheSummary.totalInputTokens)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Cache Read Tokens
                  </Typography>
                  <Typography variant="h6">
                    {formatTokenCount(cacheSummary.totalCacheReadTokens)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Savings
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {formatTokenCount(cacheSummary.totalSavings)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Cache Hit Rate
                  </Typography>
                  <Typography variant="h6">
                    {cacheSummary.totalInputTokens > 0
                      ? `${((cacheSummary.totalCacheReadTokens / cacheSummary.totalInputTokens) * 100).toFixed(1)}%`
                      : "0%"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Conversations List */}
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Conversations ({pagination.totalCount})
              </Typography>
              <IconButton onClick={() => fetchConversations(true)}>
                <RefreshIcon />
              </IconButton>
            </Box>
            
            <List sx={{ maxHeight: '1200px', overflow: 'auto' }}>
              {conversations.map((conversation, index) => (
                <ListItem
                  key={conversation.id}
                  selected={selectedConversation?.id === conversation.id}
                  onClick={() => handleConversationSelect(conversation)}
                  ref={index === conversations.length - 1 ? lastConversationElementRef : null}
                  sx={{ 
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    {/* Title */}
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      {conversation.id ? `Conversation ${conversation.id}` : 'Untitled Conversation'}
                    </Typography>
                    
                    {/* Timestamp */}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {formatTimestamp(conversation.createdAt)}
                    </Typography>
                    
                    {/* Stats Chips */}
                    <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={`${conversation.messageCount} messages`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`${formatTokenCount(conversation.totalTokens)} tokens`}
                        variant="outlined"
                        color="primary"
                      />
                      {conversation.cachedTokenSavings > 0 && (
                        <Chip
                          size="small"
                          label={`${formatTokenCount(conversation.cachedTokenSavings)} saved`}
                          variant="outlined"
                          color="success"
                        />
                      )}
                    </Box>
                    
                    {/* Preview Text */}
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                        fontStyle: 'italic',
                        maxWidth: '100%'
                      }}
                    >
                      "{generatePreview(conversation)}"
                    </Typography>
                  </Box>
                </ListItem>
              ))}
              {loadingMore && (
                <ListItem>
                  <Box display="flex" justifyContent="center" width="100%">
                    <CircularProgress size={20} />
                  </Box>
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* Conversation Detail */}
      <Grid item xs={12} md={9}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Conversation Details
            </Typography>
            
            {detailLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : selectedConversation ? (
              <Box sx={{ height: '1200px', overflow: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  Messages ({messages.length})
                </Typography>
                
                <Box>
                  {messages.map((message, index) => (
                    <Box
                      key={message.id}
                      ref={index === messages.length - 1 ? lastMessageElementRef : null}
                      mb={2}
                      p={2}
                      bgcolor={message.role === 'user' ? 'grey.100' : 'blue.50'}
                      borderRadius={1}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Chip
                          size="small"
                          label={message.role}
                          color={message.role === 'user' ? 'primary' : 'secondary'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(message.timestamp)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2">
                        {typeof message.content === 'string' 
                          ? message.content 
                          : <ExpandableJson data={message.content} />
                        }
                      </Typography>
                    </Box>
                  ))}
                  
                  {loadingMoreMessages && (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a conversation to view details
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default withStyles(styleSheet)(ConversationHistory);
